import React, {FC, useEffect, useMemo, useState} from 'react'
import InputField from '../../components/InputField'
import CkeditorField from '../../../common/CkeditorField'
import {IProductProps, PRODUCT_NAME_MAX_LENGTH, productActionType} from '../../cors/reducers'
import CheckboxField from './CheckboxField'
import {useIntl} from 'react-intl'
import {useNavigate, useParams} from 'react-router-dom'
import {toAbsoluteUrl} from '../../../../../_metronic/helpers'
import BarcodeCompany from './BarcodeCompany'
import ProductMoreImages from './ProductMoreImages'
import ProductDataTab from '../ProductDataTab'
import ProductDiscountTab from '../ProductDiscountTab'
import VariationsField from './VariationsField'
// Reuse the Add page's category UI (search box + drill-down card grid) so the
// edit category section looks identical to "Add a single product". It shares
// the same cors reducer/requests, so it updates categoryID/categoryName the
// same way the edit save reads them.
import CategoryStep from '../../add-product/general/CategoryStep'
import {tenantTaxEnabled} from '../../../../tenant/tenantState'

/**
 * Edit-product form, redesigned to match the Add-a-single-product page's
 * `aps-` look: a clickable section stepper (Category › Product Info ›
 * Description › Images › Product Details › Discounts) over a single card,
 * instead of the old react-bootstrap tabs.
 *
 * UI ONLY — every field still binds to the same `prodState`/`prodDispatch`
 * reducer, and the actual save/validation/API stays in the parent
 * ProductGeneralTab (header "Save Changes" → saveSignal → saveProduct). This
 * component never calls the save API; it only navigates sections and surfaces
 * a per-section "needs attention" icon so the editor sees where required
 * fields are still missing. `key2` lets the parent jump the form to a section
 * (e.g. "variations") when a save fails there.
 */

type SectionKey = 'category' | 'about' | 'variations' | 'productdescription' | 'image' | 'datatab' | 'discounts'

const ProductGeneralForm: FC<IProductProps> = ({
  prodState,
  prodDispatch,
  key2,
}) => {
  const {formatMessage} = useIntl()
  const {productID} = useParams()
  const navigate = useNavigate()

  const [isChecked, setIsChecked] = useState(false)
  const [activeSection, setActiveSection] = useState<SectionKey>('about')

  const hasVariations = isChecked || prodState.variations.length > 0

  // ── Section list (order = stepper order). Variations / Images / Discounts
  //    stay conditional, exactly as the old tabs were. ─────────────────────────
  const sections = useMemo<{key: SectionKey; label: string}[]>(() => {
    const list: {key: SectionKey; label: string}[] = [
      {key: 'category', label: formatMessage({id: 'category_selection'}) || 'Category Selection'},
      {key: 'about', label: formatMessage({id: 'product_info_step'}) || 'Product Info'},
    ]
    if (hasVariations) {
      list.push({key: 'variations', label: formatMessage({id: 'variations'})})
    }
    list.push({key: 'productdescription', label: formatMessage({id: 'desc'}) || 'Description'})
    if (prodState.variations.length === 0) {
      list.push({key: 'image', label: formatMessage({id: 'images'}) || 'Images'})
    }
    list.push({key: 'datatab', label: formatMessage({id: 'product_details_step'}) || 'Product Details'})
    if (prodState.product_specials.length > 0) {
      list.push({key: 'discounts', label: formatMessage({id: 'discounts_label'}) || 'Discounts'})
    }
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVariations, prodState.variations.length, prodState.product_specials.length])

  // Parent (ProductGeneralTab) bumps `key2` to focus a section on a failed save
  // — most importantly 'variations'. Honour it when it's a real section.
  useEffect(() => {
    if (key2 && sections.some((s) => s.key === key2)) {
      setActiveSection(key2 as SectionKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key2])

  // If the active section disappears (e.g. variations unchecked while on it),
  // fall back to Product Info so the card never renders empty.
  useEffect(() => {
    if (!sections.some((s) => s.key === activeSection)) {
      setActiveSection('about')
    }
  }, [sections, activeSection])

  // ── Field handlers (same wiring as before) ─────────────────────────────────
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {id, value} = e.target
    prodDispatch({type: productActionType.text, payload: {id, value}})
  }

  const handleChangeArabic = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {id, value} = e.target
    prodDispatch({type: productActionType.text, payload: {id, value: value.replace(/[A-Za-z]/g, '')}})
  }

  const resetVariations = () => {
    prodState.variations = []
    prodDispatch({type: productActionType.updateVariations, payload: prodState.variations})
  }

  const handleCheckboxChange = (checked: boolean) => {
    setIsChecked(checked)
    if (!checked) resetVariations()
  }

  // ── Per-section validation (drives the red "needs attention" icon) ─────────
  const validateVariations = (): boolean => {
    let err = false
    prodState.variations.forEach((v) => {
      if (!v.barcode || !v.color_id || !v.size_id) err = true
      if (!v.quantity || v.quantity <= '0') err = true
      if (!v.price || v.price <= '0') err = true
    })
    if (prodState.variationImages.length !== prodState.variations.length) err = true
    return err
  }

  const sectionHasError = (key: SectionKey): boolean => {
    switch (key) {
      case 'category':
        return !prodState.categoryID.value
      case 'about':
        return !prodState.model.value || !prodState.productNameEn.value || !prodState.productNameAr.value ||
          prodState.productNameEn.error || prodState.productNameAr.error ||
          !prodState.aboutEn.value || !prodState.aboutAr.value
      case 'variations':
        return validateVariations()
      case 'productdescription':
        return !prodState.productDescriptionEn.value || !prodState.productDescriptionAr.value ||
          prodState.productDescriptionEn.error || prodState.productDescriptionAr.error
      case 'image':
        if (typeof prodState.image.value === 'string' && prodState.image.value.includes('logos/img-placeholder.png')) return true
        return prodState.image.error
      case 'datatab':
        return !prodState.manufacturer_id.value || (tenantTaxEnabled() && !prodState.tax_class_id.value) ||
          (!prodState.quantity.value && prodState.variations.length === 0) ||
          (!prodState.salePrice.value && prodState.variations.length === 0)
      default:
        return false
    }
  }

  // ── Section nav (clickable stepper + Prev/Next convenience) ────────────────
  const activeIndex = sections.findIndex((s) => s.key === activeSection)
  const goPrev = () => { if (activeIndex > 0) setActiveSection(sections[activeIndex - 1].key) }
  const goNext = () => { if (activeIndex < sections.length - 1) setActiveSection(sections[activeIndex + 1].key) }

  // ── Section content ─────────────────────────────────────────────────────────
  const renderSection = () => {
    switch (activeSection) {
      case 'category':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'select_category'}) || 'Select Category'}</h3>
            {/* No setCurrentStep: picking a category just updates it in place;
                the section stepper above handles navigation (this is edit, not
                the forward-only add wizard). */}
            <CategoryStep prodState={prodState} prodDispatch={prodDispatch} />
          </>
        )

      case 'about':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'product_info_step'}) || 'Product Information'}</h3>

            <BarcodeCompany prodState={prodState} prodDispatch={prodDispatch} />

            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <InputField
                  hasError={prodState.productNameEn.error}
                  value={prodState.productNameEn.value}
                  errorMsg={prodState.productNameEn.msg}
                  isRequired={true}
                  onChange={handleChange}
                  label={formatMessage({id: 'prod_name_en'})}
                  hint={formatMessage({id: 'prod_en_hint'})}
                  id='productNameEn'
                  maxLength={PRODUCT_NAME_MAX_LENGTH}
                />
              </div>
              <div className='aps-field-half'>
                <InputField
                  hasError={prodState.productNameAr.error}
                  errorMsg={prodState.productNameAr.msg}
                  value={prodState.productNameAr.value}
                  hint={formatMessage({id: 'prod_ar_hint'})}
                  isRequired={true}
                  label={formatMessage({id: 'prod_name_ar'})}
                  id='productNameAr'
                  onChange={handleChangeArabic}
                  maxLength={PRODUCT_NAME_MAX_LENGTH}
                />
              </div>
            </div>

            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <CkeditorField
                  id='aboutEn'
                  isRequired={true}
                  label={formatMessage({id: 'about_prod_en'})}
                  hint={formatMessage({id: 'about_prod_hint'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.aboutEn.value}
                  hasError={prodState.aboutEn.error}
                  errorMsg={prodState.aboutEn.msg}
                />
              </div>
              <div className='aps-field-half'>
                <CkeditorField
                  id='aboutAr'
                  isRequired={true}
                  label={formatMessage({id: 'about_prod_ar'})}
                  hint={formatMessage({id: 'about_prod_hint'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.aboutAr.value}
                  hasError={prodState.aboutAr.error}
                  errorMsg={prodState.aboutAr.msg}
                />
              </div>
            </div>

            {(!productID || String(productID).includes('&')) && (
              <div className='mt-20'>
                <CheckboxField
                  isChecked={isChecked || prodState.variations.length > 0}
                  setIsChecked={handleCheckboxChange}
                />
              </div>
            )}
          </>
        )

      case 'variations':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'variations'})}</h3>
            <VariationsField prodState={prodState} prodDispatch={prodDispatch} />
          </>
        )

      case 'productdescription':
        return (
          <>
            <h3 className='aps-step-card-title'>
              {formatMessage({id: 'marketing_description'}) || formatMessage({id: 'desc'})}
            </h3>
            <div className='aps-field-row mt-20'>
              <div className='aps-field-half'>
                <CkeditorField
                  id='productDescriptionEn'
                  isRequired={true}
                  label={formatMessage({id: 'desc_en'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.productDescriptionEn.value}
                  hasError={prodState.productDescriptionEn.error}
                  errorMsg={prodState.productDescriptionEn.msg}
                />
              </div>
              <div className='aps-field-half'>
                <CkeditorField
                  id='productDescriptionAr'
                  isRequired={true}
                  label={formatMessage({id: 'desc_ar'})}
                  prodDispatch={prodDispatch}
                  prodState={prodState}
                  value={prodState.productDescriptionAr.value}
                  hasError={prodState.productDescriptionAr.error}
                  errorMsg={prodState.productDescriptionAr.msg}
                />
              </div>
            </div>
          </>
        )

      case 'image':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'images'}) || 'Product Images'}</h3>
            {prodState.variations.length === 0 ? (
              <ProductMoreImages prodState={prodState} prodDispatch={prodDispatch} />
            ) : (
              <p className='aps-step-hint'>
                {formatMessage({id: 'images_per_variation'}) || 'Images are managed per variation.'}
              </p>
            )}
          </>
        )

      case 'datatab':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'product_details_step'}) || 'Product Details'}</h3>
            <ProductDataTab prodState={prodState} prodDispatch={prodDispatch} isChecked={isChecked} />
          </>
        )

      case 'discounts':
        return (
          <>
            <h3 className='aps-step-card-title'>{formatMessage({id: 'discounts_label'}) || 'Discounts'}</h3>
            <ProductDiscountTab prodState={prodState} prodDispatch={prodDispatch} />
          </>
        )

      default:
        return null
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className='aps-wizard'>
      {/* Clickable section stepper — every section is reachable in edit mode. */}
      <div className='aps-step-breadcrumb'>
        {sections.map((s, idx) => (
          <React.Fragment key={s.key}>
            <button
              type='button'
              className={`aps-step-item aps-step-item--clickable ${
                s.key === activeSection ? 'aps-step-active' : 'aps-step-inactive'
              }`}
              onClick={() => setActiveSection(s.key)}
            >
              {s.label}
              {sectionHasError(s.key) && (
                <img
                  className='aps-step-err-icon'
                  width={16}
                  src={toAbsoluteUrl('/media/logos/error.svg')}
                  alt=''
                />
              )}
            </button>
            {idx < sections.length - 1 && <span className='aps-step-chevron'>›</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Active section content */}
      <div className='aps-step-card'>
        {renderSection()}

        {/* Section-to-section navigation. The actual save lives in the page
            header ("Save Changes"); this is just convenience movement. */}
        <div className='aps-step-nav-row'>
          <button type='button' className='aps-prev-btn' onClick={() => navigate('/products/list')}>
            {formatMessage({id: 'cancel'}) || 'Cancel'}
          </button>
          <div style={{display: 'flex', gap: 12, marginLeft: 'auto'}}>
            {activeIndex > 0 && (
              <button type='button' className='aps-prev-btn' onClick={goPrev}>
                {formatMessage({id: 'previous_step'}) || '← Previous'}
              </button>
            )}
            {activeIndex < sections.length - 1 && (
              <button type='button' className='aps-next-btn' onClick={goNext}>
                {formatMessage({id: 'next'}) || 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductGeneralForm
